import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CellularBillingService } from 'src/app/shared/services/cellular-billing.service';
import { OverlayBundleRecord } from 'src/app/shared/model/cellular-billing';
import { MatDialog } from '@angular/material/dialog';
import { ConfigurationDeleteDialogComponent } from '../dialogs/configuration-delete-dialog.component';

@Component({ selector: 'app-overlay-bundles', templateUrl: './overlay-bundles.component.html', styleUrls: ['./overlay-bundles.component.scss'] })
export class OverlayBundlesComponent implements OnInit, OnDestroy {
  @Input() disabled = false;
  @Output() changed = new EventEmitter<void>();
  @ViewChild(MatSort, { static: false }) set sort(value: MatSort) { this.data.sort = value; }
  readonly columns = ['customer', 'site', 'hostname', 'code', 'productLabel', 'startDate', 'endDate', 'listPriceCents', 'actions'];
  readonly data = new MatTableDataSource<OverlayBundleRecord>([]);
  readonly products = [
    { code: 'OVERPP100GB', label: 'Overlay prepaid bundle 100GB' },
    { code: 'OVERPP500GB', label: 'Overlay prepaid bundle 500GB' }
  ];
  readonly form = this.fb.group({
    customer: ['', [Validators.required, Validators.maxLength(200)]], site: ['', Validators.required], hostname: [''],
    code: ['OVERPP100GB', Validators.required], startDate: ['', Validators.required], endDate: [''],
    listPrice: [null, [Validators.required, Validators.min(0)]]
  });
  loading = false;
  saving = false;
  adding = false;
  error = '';
  saveError = '';
  success = '';
  editing: OverlayBundleRecord;
  private readonly subscriptions = new Subscription();
  constructor(private fb: FormBuilder, private billing: CellularBillingService, private dialog: MatDialog) { }
  ngOnInit(): void { this.load(); }
  ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
  load(): void {
    this.loading = true; this.error = '';
    this.subscriptions.add(this.billing.listOverlayBundles().pipe(finalize(() => this.loading = false))
      .subscribe(rows => this.data.data = rows, () => this.error = 'Unable to load Overlay bundles. Try again.'));
  }
  search(value: string): void { this.data.filter = value.trim().toLowerCase(); }
  openForm(): void { this.editing = undefined; this.form.reset({ code: 'OVERPP100GB' }); this.adding = !this.adding; }
  edit(row: OverlayBundleRecord): void {
    if (this.saving || this.disabled) return;
    this.editing = row; this.adding = true; this.saveError = '';
    this.form.reset({ customer: row.customer, site: row.site, hostname: row.hostname, code: row.code,
      startDate: row.startDate, endDate: row.endDate || '', listPrice: row.listPriceCents / 100 });
  }
  remove(row: OverlayBundleRecord): void {
    if (this.saving || this.disabled) return;
    const ref = this.dialog.open(ConfigurationDeleteDialogComponent, { width: '480px', maxWidth: 'calc(100vw - 32px)', data: { kind: 'Overlay bundle', label: `${row.customer} · ${row.site} · ${row.code}` } });
    this.subscriptions.add(ref.afterClosed().subscribe(confirmed => {
      if (!confirmed || this.saving || this.disabled) return;
      this.saving = true; this.saveError = ''; this.error = '';
      this.subscriptions.add(this.billing.deleteConfiguration('overlay-bundles', row._id, row.revision || 0).pipe(finalize(() => this.saving = false)).subscribe(() => {
        this.success = 'Bundle deleted from active assignments. History retained.'; this.adding = false; this.editing = undefined;
        this.load(); this.changed.emit();
      }, error => this.error = error && error.error && error.error.error && error.error.error.message || 'Unable to delete bundle.'));
    }));
  }
  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving || this.disabled) return;
    this.saving = true; this.saveError = ''; this.success = '';
    const operation = this.editing ? this.billing.updateConfiguration('overlay-bundles', this.editing._id, this.editing.revision || 0, this.form.value) : this.billing.createOverlayBundle(this.form.value);
    this.subscriptions.add(operation.pipe(finalize(() => this.saving = false))
      .subscribe(() => {
        this.adding = false; this.editing = undefined; this.form.reset({ code: 'OVERPP100GB' });
        this.success = 'Bundle saved. Site MAC will be matched against Overlay usage for its active dates.';
        this.load(); this.changed.emit();
      }, error => this.saveError = error && error.error && error.error.error && error.error.error.message || 'Unable to save this bundle.'));
  }
}
